import { Group } from "@fairfleet/geotab";

/**
 * The part of a group the index needs. Full `Group` objects fit, and so do lighter rows such as
 * security clearances.
 */
export interface GroupNode {
  id: string;
  children?: ReadonlyArray<{ id: string }>;
}

export interface GroupIndex<G extends GroupNode = Group> {
  byId: Map<string, G>;
  parentById: Map<string, string>;
}

/** Indexes Geotab's group tree, which only links parents to children. */
export function buildGroupIndex<G extends GroupNode = Group>(groups: G[] = []): GroupIndex<G> {
  const byId = new Map<string, G>();
  const parentById = new Map<string, string>();
  for (const group of groups) {
    byId.set(group.id, group);
    for (const child of group.children ?? []) {
      parentById.set(child.id, group.id);
    }
  }
  return { byId, parentById };
}

/**
 * The group and all its ancestors that are in the index, nearest first. Stops at a cycle, so a
 * malformed tree cannot hang the caller.
 */
export function groupAncestry<G extends GroupNode>(groupId: string, index: GroupIndex<G>): G[] {
  const chain: G[] = [];
  const seen = new Set<string>();
  let current: string | undefined = groupId;
  while (current !== undefined && !seen.has(current)) {
    seen.add(current);
    const group = index.byId.get(current);
    if (group) chain.push(group);
    current = index.parentById.get(current);
  }
  return chain;
}

/**
 * True when `groupId` is `rootId` or one of its descendants. Walks the parent links only, so it
 * also works for ids that are not in `byId`. Cycle-safe.
 */
export function isUnderBranch(
  index: Pick<GroupIndex<GroupNode>, "parentById">,
  groupId: string,
  rootId: string,
): boolean {
  const seen = new Set<string>();
  let current: string | undefined = groupId;
  while (current !== undefined && !seen.has(current)) {
    if (current === rootId) return true;
    seen.add(current);
    current = index.parentById.get(current);
  }
  return false;
}

/** Geotab system groups carry readable ids such as `GroupCompanyId`; customer groups are encoded. */
export function isGeotabSystemGroup(groupId: string): boolean {
  return groupId.startsWith("Group");
}

/**
 * The given group ids followed by the ids of all their descendants, each once. A search by group
 * id is not documented to include subgroups, so callers expand the selection themselves.
 * Follows child links even to ids without a row of their own. Cycle-safe.
 */
export function groupIdsWithDescendants(groupIds: string[], groups: GroupNode[] = []): string[] {
  const childrenById = new Map(groups.map(group => [group.id, group.children ?? []]));
  const result: string[] = [];
  const seen = new Set<string>();
  const queue = [...groupIds];
  for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
    if (seen.has(next)) continue;
    seen.add(next);
    result.push(next);
    for (const child of childrenById.get(next) ?? []) queue.push(child.id);
  }
  return result;
}
